import { Button, Select } from '@react-apps/design-system';
import { UFS } from '../../domain/entities/content.entity';
import { useInstitutionsById } from '../hooks/use-institutions';
import { useExamFilterOptions } from '../hooks/use-exam-search';
import {
  changeInstitution,
  changeUf,
  changeVariant,
  changeYear,
  type ExamSearchFilters,
} from '../exam-search-filters';

/**
 * Os filtros da busca de provas (DESEN-41), em cadeia: **UF → Instituição →
 * Ano → Caderno**. Controlado por props: não sabe onde o estado mora (a tela de
 * busca guarda na URL; o picker de prova, em `useState`).
 *
 * - Cada select fica desabilitado, dizendo o que falta, até o elo anterior
 *   ser escolhido. O Caderno libera junto com o Ano, sem precisar dele.
 * - A Instituição lista só as **vivas** da UF.
 * - Ano e Caderno vêm do back (`exam-filter-options`): os da instituição, e os
 *   cadernos só do ano quando ele está escolhido. Se as opções falham, os dois
 *   mostram o erro com "tentar de novo", e a lista segue com os outros filtros.
 *
 * Só `Select` do design-system. A primeira opção de cada um ("Todas"/"Todos")
 * é o "sem filtro": o `placeholder` do `Select` é uma opção desabilitada, e com
 * ele não daria para voltar atrás.
 */
export function ExamFilters({
  value,
  onChange,
}: {
  value: ExamSearchFilters;
  onChange: (next: ExamSearchFilters) => void;
}) {
  const institutions = useInstitutionsById();
  const options = useExamFilterOptions(value.institutionId, value.year);

  const institutionOptions = value.uf
    ? [...(institutions.data?.values() ?? [])]
        .filter((i) => !i.deletedAt && i.uf === value.uf)
        .sort((a, b) => a.initials.localeCompare(b.initials, 'pt'))
        .map((i) => ({ value: i.id, label: `${i.initials} – ${i.name}` }))
    : [];

  const noInstitution = value.institutionId === '';
  const optionsLoading = !noInstitution && options.isLoading;
  const optionsError = !noInstitution && options.isError;
  const dependentHelper = noInstitution
    ? 'Escolha a instituição primeiro'
    : optionsLoading
      ? 'Carregando…'
      : undefined;
  const errorText = optionsError
    ? 'Não foi possível carregar as opções.'
    : undefined;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="UF"
          value={value.uf}
          onChange={(e) => onChange(changeUf(e.target.value))}
          options={[
            { value: '', label: 'Todas' },
            ...UFS.map((uf) => ({ value: uf, label: uf })),
          ]}
        />
        <Select
          label="Instituição"
          value={value.institutionId}
          disabled={!value.uf || institutions.isLoading}
          helperText={
            !value.uf
              ? 'Escolha a UF primeiro'
              : institutions.isLoading
                ? 'Carregando…'
                : undefined
          }
          onChange={(e) => onChange(changeInstitution(value, e.target.value))}
          options={[{ value: '', label: 'Todas' }, ...institutionOptions]}
        />
        <Select
          label="Ano"
          value={value.year}
          disabled={noInstitution || optionsLoading || optionsError}
          helperText={dependentHelper}
          error={errorText}
          onChange={(e) => onChange(changeYear(value, e.target.value))}
          options={[
            { value: '', label: 'Todos' },
            ...(options.data?.years ?? []).map((y) => ({
              value: String(y),
              label: String(y),
            })),
          ]}
        />
        <Select
          label="Caderno"
          value={value.variant}
          disabled={noInstitution || optionsLoading || optionsError}
          helperText={dependentHelper}
          error={errorText}
          onChange={(e) => onChange(changeVariant(value, e.target.value))}
          options={[
            { value: '', label: 'Todos' },
            ...(options.data?.variants ?? []).map((v) => ({
              value: v,
              label: v,
            })),
          ]}
        />
      </div>
      {optionsError && (
        <Button
          type="button"
          variant="secondary"
          size="xs"
          onClick={() => void options.refetch()}
        >
          Tentar de novo
        </Button>
      )}
    </div>
  );
}
